import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';

import { MatDialogSizes } from '../../../../enums/mat-dialog-sizes.enum';
import { SuperStarComponent } from '../../../app-layout/shared/super-star/super-star.component';
import {
    EntityHistoryDialogComponent,
    EntityHistoryDialogInput,
} from './entity-history-dialog.component';
import { EntityHistorySubjectType } from './entity-history.types';

/**
 * Drop-in "History" button. Caller passes `[canView]` (typically `isSuper`).
 * Renders nothing when `canView` is false.
 *
 * Two visual variants:
 *   - `variant="v2"` (default): proper V2 mat-flat-button with Phosphor icon
 *     (per `_how to/UI_V2_BUTTONS.md`). Use on V2-layout pages.
 *   - `variant="v1"`: plain Bootstrap `btn btn-outline-secondary` matching
 *     legacy page button rows (Share / Edit / etc.). No icon — keeps the
 *     button visually identical to its V1 siblings.
 *
 * Usage:
 *   <app-entity-history-button
 *       subjectType="document"
 *       [subjectId]="doc.id"
 *       [subjectLabel]="doc.fileName"
 *       [canView]="isSuper"
 *       [showSuperStar]="true"
 *       variant="v1">           <!-- omit / "v2" for V2 pages -->
 *   </app-entity-history-button>
 */
@Component({
    selector: 'app-entity-history-button',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, MatButtonModule, SuperStarComponent],
    template: `
        <ng-container *ngIf="canView">
            <!-- V2 variant: mat-flat-button + Phosphor icon -->
            <button *ngIf="variant === 'v2'"
                    type="button"
                    mat-flat-button
                    color="accent"
                    [disableRipple]="true"
                    class="entity-history-btn"
                    (click)="open()">
                <i class="ph-bold ph-clock-counter-clockwise icon-text icon-16" aria-hidden="true"></i>
                <span>History</span>
                <app-super-star *ngIf="showSuperStar"></app-super-star>
            </button>

            <!-- V1 variant: bootstrap btn-outline-secondary, no icon -->
            <button *ngIf="variant === 'v1'"
                    type="button"
                    class="btn btn-outline-secondary"
                    (click)="open()">
                History
                <app-super-star *ngIf="showSuperStar"></app-super-star>
            </button>
        </ng-container>
    `,
    styles: [`
        /* Custom-element hosts default to display: inline, which makes Bootstrap
           margin classes (me-*, ms-*) and standalone margins inert. Set the
           host inline-block so callers can space the component normally. */
        :host {
            display: inline-block;
        }

        /* V2 variant: mat-flat-button. */
        .entity-history-btn {
            display: inline-flex;
            align-items: center;
            gap: 6px;
        }

        /* V1 variant: bootstrap btn. Just give it the border + flex alignment.
           Spacing is the caller's responsibility — wrap or class the component
           tag with the margin you need for its position in the button row. */
        :host .btn.btn-outline-secondary {
            border: 1px solid #d5dae2;
            display: inline-flex;
            align-items: center;
            gap: 6px;
        }
    `],
})
export class EntityHistoryButtonComponent
{
    @Input() subjectType!: EntityHistorySubjectType;
    @Input() subjectId!: number;
    @Input() subjectLabel?: string;
    @Input() showSuperStar = false;

    /** Visual variant — `v2` (default) or legacy bootstrap `v1`. */
    @Input() variant: 'v1' | 'v2' = 'v2';

    /** Permission gate — pass `true` to render the button. */
    @Input() canView = false;

    constructor(private _dialog: MatDialog)
    {
    }

    open(): void
    {
        if (!this.subjectType || !this.subjectId) {
            return;
        }

        const data: EntityHistoryDialogInput = {
            subjectType: this.subjectType,
            subjectId: this.subjectId,
            subjectLabel: this.subjectLabel,
        };

        this._dialog.open(EntityHistoryDialogComponent, {
            width: MatDialogSizes.XL,
            maxWidth: '95vw',
            position: { top: '50px' },
            panelClass: 'entity-history-dialog',
            autoFocus: 'dialog',
            data,
        });
    }
}
